/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: roleProfilesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API functions for Role Profiles module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import {
  ListRoleProfilesResponse,
  RoleProfileResponse,
  RoleProfile,
  RoleProfileFormData,
  QuickListRoleProfile
} from '../types';

export interface FetchRoleProfilesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}

/**
 * Fetch role profiles list with pagination and filters
 */
export async function fetchRoleProfiles(
  params: FetchRoleProfilesParams,
  tenantSlug: string,
  userId?: string | number | null
): Promise<ListRoleProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search })
  });

  const response = await fetch(
    `${buildApiUrl(API_CONFIG.ENDPOINTS.ROLE_PROFILES)}?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  return response.json();
}

/**
 * Fetch role profile by ID
 */
export async function fetchRoleProfileById(
  id: string,
  tenantSlug: string,
  userId?: string | number | null
): Promise<RoleProfile> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.ROLE_PROFILES}/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result: RoleProfileResponse = await response.json();
  return result.data;
}

/**
 * Create role profile
 */
export async function createRoleProfile(
  data: RoleProfileFormData,
  tenantSlug: string,
  userId?: string | number | null
): Promise<RoleProfile> {
  const response = await fetch(
    buildApiUrl(API_CONFIG.ENDPOINTS.ROLE_PROFILES),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug, userId),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result: RoleProfileResponse = await response.json();
  return result.data;
}

/**
 * Update role profile
 */
export async function updateRoleProfile(
  id: string,
  data: Partial<RoleProfileFormData>,
  tenantSlug: string,
  userId?: string | number | null
): Promise<RoleProfile> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.ROLE_PROFILES}/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(tenantSlug, userId),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result: RoleProfileResponse = await response.json();
  return result.data;
}

/**
 * Delete role profile
 */
export async function deleteRoleProfile(
  id: string,
  tenantSlug: string,
  userId?: string | number | null
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.ROLE_PROFILES}/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }
}

/**
 * Duplicate role profile
 */
export async function duplicateRoleProfile(
  id: string,
  tenantSlug: string,
  userId?: string | number | null
): Promise<RoleProfile> {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.ROLE_PROFILES}/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug, userId)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result: RoleProfileResponse = await response.json();
  return result.data;
}

/**
 * Fetch quick list of role profiles
 */
export async function fetchQuickListRoleProfiles(
  search?: string,
  tenantSlug?: string,
  userId?: string | number | null
): Promise<QuickListRoleProfile[]> {
  const queryParams = new URLSearchParams();
  if (search) {
    queryParams.append('search', search);
  }

  const response = await fetch(
    `${buildApiUrl(`${API_CONFIG.ENDPOINTS.ROLE_PROFILES}/quicklist`)}?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId)
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result: { data: QuickListRoleProfile[] } = await response.json();
  return result.data;
}

